<template>
  <el-row :gutter="15">
    <el-col
      :xs="7"
      :sm="6"
      :md="6"
      :lg="5"
      :xl="5"
    >
      <Filters
        v-model="filters"
        :resource="$sdk.asset"
        :primary-fields="[ 'serial', 'manufacturer_model.asset_manufacturer', 'manufacturer_model', 'status', 'branch', 'events.account' ]"
        @ready="load"
        @apply="reload"
      />
    </el-col>

    <el-col
      :xs="17"
      :sm="18"
      :md="18"
      :lg="19"
      :xl="19"
    >
      <el-card
        v-loading="isLoading"
        shadow="never"
      >
        <AssetsTable :assets="assets">
          <template v-slot:peek="{ asset }">
            <AssetPeek :asset="asset" />
          </template>

          <template v-slot:actions="{ asset }">
            <el-dropdown
              split-button
              size="small"
              trigger="click"
              @command="command => command(asset)"
              @click="editAsset(asset)"
            >
              Edit
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item
                  :disabled="!canReturnAsset(asset)"
                  :command="returnAsset"
                >
                  Return into Stock
                </el-dropdown-item>

                <el-dropdown-item
                  :disabled="!canAllocateAsset(asset)"
                  :command="allocateAsset"
                >
                  Allocate to Customer
                </el-dropdown-item>

                <el-dropdown-item
                  :disabled="!canTransferAsset(asset)"
                  divided
                  :command="transferAsset"
                >
                  Interbranch Transfer
                </el-dropdown-item>

                <el-dropdown-item
                  :disabled="!canWarrantyClaimAsset(asset)"
                  :command="warrantyClaimAsset"
                >
                  Log Warranty Claim
                </el-dropdown-item>

                <el-dropdown-item
                  :disabled="!canDeclareUnrepairableAsset(asset)"
                  :command="brokenAsset"
                >
                  Declare Unrepairable
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </template>
        </AssetsTable>

        <el-pagination
          class="text-center pt-3"
          :current-page.sync="assets.page"
          :page-size.sync="assets.limit"
          layout="total, sizes, prev, pager, next, jumper"
          :total="assets.total"
          @size-change="reload"
          @current-change="reload"
        />
      </el-card>
    </el-col>
  </el-row>
</template>

<script>
import Filters from '@/components/Filters/Simplified'
import AssetsTable from '@/components/Application/Manage/Assets/Table'
import AssetPeek from '@/components/Application/Manage/Assets/Asset/Peek'
import AssetCreateDialog from '@/components/Application/Manage/Assets/Asset/CreateDialog'
import AssetEditDialog from '@/components/Application/Manage/Assets/Asset/EditDialog'
import AssetReturnDialog from '@/components/Application/Manage/Assets/Asset/ReturnDialog'
import AssetAllocateDialog from '@/components/Application/Manage/Assets/Asset/AllocateDialog'
import AssetTransferDialog from '@/components/Application/Manage/Assets/Asset/TransferDialog'
import AssetWarrantyClaimDialog from '@/components/Application/Manage/Assets/Asset/WarrantyClaimDialog'
import AssetBrokenDialog from '@/components/Application/Manage/Assets/Asset/BrokenDialog'
import { STATUS_ALLOCATED, STATUS_IN_STOCK, STATUS_WARRANTY, STATUS_BROKEN } from '@/plugins/sdk/resources/Asset'
import { CREATED, UPDATED, DELETED, RESTORED } from '@/plugins/sdk/Events'
import { CREATE } from '@/plugins/user'
import Vue from 'vue'

export default {
  components: {
    Filters,
    AssetsTable,
    AssetPeek
  },
  data () {
    return {
      title: 'Assets',
      breadcrumb: {
        title: 'Asset',
        name: 'Assets',
        route: {
          name: 'manage-assets'
        }
      },
      actions: [
        {
          hidden: this.$user.cannot(CREATE, this.$sdk.asset),
          name: 'Create',
          icon: ['fal', 'plus'],
          event: () => {
            this.createAsset(new this.$sdk.asset())
          }
        },
        {
          name: 'Refresh',
          icon: ['fal', 'sync'],
          event: () => this.reload()
        }
      ],
      isLoading: false,
      assetCreateDialogInstance: undefined,
      assetCreateDialogVisible: false,
      assetEditDialogInstance: undefined,
      assetEditDialogVisible: false,
      assetReturnDialogInstance: undefined,
      assetReturnDialogVisible: false,
      assetAllocateDialogInstance: undefined,
      assetAllocateDialogVisible: false,
      assetTransferDialogInstance: undefined,
      assetTransferDialogVisible: false,
      assetWarrantyClaimDialogInstance: undefined,
      assetWarrantyClaimDialogVisible: false,
      assetBrokenDialogInstance: undefined,
      assetBrokenDialogVisible: false,
      filters: [],
      assets: []
    }
  },
  watch: {
    assetCreateDialogVisible (visible) {
      if (!visible && typeof this.assetCreateDialogInstance !== 'undefined') {
        this.assetCreateDialogInstance.$destroy()
        this.assetCreateDialogInstance.$el.remove()
        this.assetCreateDialogInstance = undefined
      }
    },
    assetEditDialogVisible (visible) {
      if (!visible && typeof this.assetEditDialogInstance !== 'undefined') {
        this.assetEditDialogInstance.$destroy()
        this.assetEditDialogInstance.$el.remove()
        this.assetEditDialogInstance = undefined
      }
    },
    assetReturnDialogVisible (visible) {
      if (!visible && typeof this.assetReturnDialogInstance !== 'undefined') {
        this.assetReturnDialogInstance.$destroy()
        this.assetReturnDialogInstance.$el.remove()
        this.assetReturnDialogInstance = undefined
      }
    },
    assetAllocateDialogVisible (visible) {
      if (!visible && typeof this.assetAllocateDialogInstance !== 'undefined') {
        this.assetAllocateDialogInstance.$destroy()
        this.assetAllocateDialogInstance.$el.remove()
        this.assetAllocateDialogInstance = undefined
      }
    },
    assetTransferDialogVisible (visible) {
      if (!visible && typeof this.assetTransferDialogInstance !== 'undefined') {
        this.assetTransferDialogInstance.$destroy()
        this.assetTransferDialogInstance.$el.remove()
        this.assetTransferDialogInstance = undefined
      }
    },
    assetWarrantyClaimDialogVisible (visible) {
      if (!visible && typeof this.assetWarrantyClaimDialogInstance !== 'undefined') {
        this.assetWarrantyClaimDialogInstance.$destroy()
        this.assetWarrantyClaimDialogInstance.$el.remove()
        this.assetWarrantyClaimDialogInstance = undefined
      }
    },
    assetBrokenDialogVisible (visible) {
      if (!visible && typeof this.assetBrokenDialogInstance !== 'undefined') {
        this.assetBrokenDialogInstance.$destroy()
        this.assetBrokenDialogInstance.$el.remove()
        this.assetBrokenDialogInstance = undefined
      }
    }
  },
  mounted () {
    this
      .$sdk
      .asset
      .on([ CREATED, UPDATED, DELETED, RESTORED ], this.reload, this)

    this
      .$sdk
      .asset_event
      .on([ CREATED, UPDATED, DELETED, RESTORED ], this.reload, this)
  },
  methods: {
    async load () {
      this.isLoading = true

      try {
        this.assets = await this
          .$sdk
          .asset
          .list(request => {
            request.filters = this.filters
            request.include = [ 'manufacturer_model.type', 'manufacturer_model.asset_manufacturer', 'branch', 'events' ]
            request.order = 'created_at:desc'
          })
      } finally {
        this.isLoading = false
      }
    },
    async reload () {
      this.isLoading = true

      try {
        await this
          .assets
          .reload(request => {
            request.filters = this.filters
          })
      } finally {
        this.isLoading = false
      }
    },
    canReturnAsset (asset) {
      return asset.status === STATUS_ALLOCATED || asset.status === STATUS_WARRANTY || asset.status === STATUS_BROKEN
    },
    canAllocateAsset (asset) {
      return asset.status === STATUS_IN_STOCK
    },
    canTransferAsset (asset) {
      return asset.status === STATUS_IN_STOCK
    },
    canWarrantyClaimAsset (asset) {
      return asset.status === STATUS_IN_STOCK || asset.status === STATUS_BROKEN
    },
    canDeclareUnrepairableAsset (asset) {
      return asset.status === STATUS_IN_STOCK || asset.status === STATUS_WARRANTY
    },
    createAsset (asset) {
      const AssetCreateDialogClass = Vue.extend(AssetCreateDialog)

      this.assetCreateDialogInstance = new AssetCreateDialogClass({
        propsData: {
          asset: asset.clone()
        }
      })

      this
        .assetCreateDialogInstance
        .$on('destroy', () => {
          this.assetCreateDialogVisible = false
        })

      this
        .assetCreateDialogInstance
        .$mount()

      this
        .$root
        .$el
        .appendChild(this.assetCreateDialogInstance.$el)

      this.assetCreateDialogVisible = true
    },
    editAsset (asset) {
      const AssetEditDialogClass = Vue.extend(AssetEditDialog)

      this.assetEditDialogInstance = new AssetEditDialogClass({
        propsData: {
          asset: asset.clone()
        }
      })

      this
        .assetEditDialogInstance
        .$on('destroy', () => {
          this.assetEditDialogVisible = false
        })

      this
        .assetEditDialogInstance
        .$mount()

      this
        .$root
        .$el
        .appendChild(this.assetEditDialogInstance.$el)

      this.assetEditDialogVisible = true
    },
    returnAsset (asset) {
      const AssetReturnDialogClass = Vue.extend(AssetReturnDialog)

      this.assetReturnDialogInstance = new AssetReturnDialogClass({
        propsData: {
          asset: asset.clone()
        }
      })

      this
        .assetReturnDialogInstance
        .$on('destroy', () => {
          this.assetReturnDialogVisible = false
        })

      this
        .assetReturnDialogInstance
        .$mount()

      this
        .$root
        .$el
        .appendChild(this.assetReturnDialogInstance.$el)

      this.assetReturnDialogVisible = true
    },
    allocateAsset (asset) {
      const AssetAllocateDialogClass = Vue.extend(AssetAllocateDialog)

      this.assetAllocateDialogInstance = new AssetAllocateDialogClass({
        propsData: {
          asset: Object.assign(asset.clone(), {
            events: []
          })
        }
      })

      this
        .assetAllocateDialogInstance
        .$on('destroy', () => {
          this.assetAllocateDialogVisible = false
        })

      this
        .assetAllocateDialogInstance
        .$mount()

      this
        .$root
        .$el
        .appendChild(this.assetAllocateDialogInstance.$el)

      this.assetAllocateDialogVisible = true
    },
    transferAsset (asset) {
      const AssetTransferDialogClass = Vue.extend(AssetTransferDialog)

      this.assetTransferDialogInstance = new AssetTransferDialogClass({
        propsData: {
          asset: asset.clone()
        }
      })

      this
        .assetTransferDialogInstance
        .$on('destroy', () => {
          this.assetTransferDialogVisible = false
        })

      this
        .assetTransferDialogInstance
        .$mount()

      this
        .$root
        .$el
        .appendChild(this.assetTransferDialogInstance.$el)

      this.assetTransferDialogVisible = true
    },
    warrantyClaimAsset (asset) {
      const AssetWarrantyClaimDialogClass = Vue.extend(AssetWarrantyClaimDialog)

      this.assetWarrantyClaimDialogInstance = new AssetWarrantyClaimDialogClass({
        propsData: {
          asset: asset.clone()
        }
      })

      this
        .assetWarrantyClaimDialogInstance
        .$on('destroy', () => {
          this.assetWarrantyClaimDialogVisible = false
        })

      this
        .assetWarrantyClaimDialogInstance
        .$mount()

      this
        .$root
        .$el
        .appendChild(this.assetWarrantyClaimDialogInstance.$el)

      this.assetWarrantyClaimDialogVisible = true
    },
    brokenAsset (asset) {
      const AssetBrokenDialogClass = Vue.extend(AssetBrokenDialog)

      this.assetBrokenDialogInstance = new AssetBrokenDialogClass({
        propsData: {
          asset: asset.clone()
        }
      })

      this
        .assetBrokenDialogInstance
        .$on('destroy', () => {
          this.assetBrokenDialogVisible = false
        })

      this
        .assetBrokenDialogInstance
        .$mount()

      this
        .$root
        .$el
        .appendChild(this.assetBrokenDialogInstance.$el)

      this.assetBrokenDialogVisible = true
    }
  }
}
</script>
